import React from "react";

import { FormattedMessage, injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import InfoFilled from "@/icons/__generated__/InfoFilled/InfoFilled20";
import PingPong from "@/icons/__generated__/PingPong/PingPong24";
import { IFaqs } from "~/types/api-types";

import {
  eventFaqDefinition,
  miscEventFaqDefinitions,
  category_labels,
} from "../data/event-faq-definitions";
import {
  eventActivitiesSelected,
  activitiesNotAllowed,
} from "../data/event-faq-display-sections";

import Description from "./Description";
import { analyticEventOnClick } from "./faq-utils";

interface EventActivitiesProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

interface EventActivitiesOptions {
  arcade_games: string;
  darts: string;
  karaoke: string;
  ping_pong_table: string;
  pool_table: string;
  shuffleboard: string;
  misc_activities: {
    description: string;
  };
}

interface ProhibitedActivityOptions {
  is_21st_bday_prohibited: string;
  is_after_party_prohibited: string;
  is_album_release_party_prohibited: string;
  is_bachelor_party_prohibited: string;
  is_concert_prohibited: string;
  is_festival_holiday_party_prohibited: string;
  is_influencer_event_prohibited: string;
  is_kids_party_prohibited: string;
  is_party_prohibited: string;
  is_sports_viewing_party_prohibited: string;
  is_wedding_prohibited: string;
  other_prohibited_activity: string;
}

const EventActivities: React.FC<EventActivitiesProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const hasOtherActivities = faqData?.misc_activities?.description?.trim();

  const availableActivities = eventActivitiesSelected(
    faqData,
  ) as (keyof EventActivitiesOptions)[];
  const prohibitedActivities = activitiesNotAllowed(
    faqData,
  ) as (keyof ProhibitedActivityOptions)[];

  const hasOtherProhibitedActivity =
    faqData["other_prohibited_activity"] &&
    faqData["other_prohibited_activity"] !== "";

  const hasSpecifiedProhibitedActivities =
    prohibitedActivities.length > 0 || hasOtherProhibitedActivity;

  const label = intl.formatMessage(category_labels.activities);

  const findAvailableActivities = (activity: string) => {
    return (
      eventFaqDefinition.event_activities?.activities_provided[
        activity as keyof EventActivitiesOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const otherActivities = () => {
    return (
      eventFaqDefinition.event_activities?.activities_provided[
        "misc_activities"
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const findAllowedActivities = (activity: string) => {
    const equipmentData =
      faqData[activity as keyof EventActivitiesOptions]?.description;
    return equipmentData ? true : false;
  };

  const findAvailableActivityDescriptions = (activity: string) => {
    const equipmentData = faqData[activity as keyof EventActivitiesOptions];
    return equipmentData ? (equipmentData.description as string) : "";
  };

  const findProhibitedActivities = (activity: string) => {
    return (
      eventFaqDefinition.event_activities?.prohibited_activities[
        activity as keyof ProhibitedActivityOptions
      ] ?? { id: "", defaultMessage: "" }
    );
  };

  return (
    <div className="EventFAQ">
      {faqData && (
        <Accordion
          type="multiple"
          onValueChange={() => {
            analyticEventOnClick(userID, listingID, "Event Activities");
          }}
        >
          <AccordionItem value="event-activity-section">
            <AccordionTrigger className="tw-py-24">
              <div className="tw-flex tw-gap-12 tw-items-center">
                <PingPong />
                <Text element="h3" typeface="headline-1-m">
                  {label}
                </Text>
              </div>
            </AccordionTrigger>
            <AccordionContent className="tw-ml-36 tw-pb-24">
              <div className="tw-space-y-8">
                {(availableActivities.length > 0 || hasOtherActivities) && (
                  <>
                    <Text typeface="body-2-m" element="p">
                      <FormattedMessage
                        id="listing.event_faq.section.activities_available"
                        defaultMessage="Activities available during booking"
                      />
                    </Text>
                    {availableActivities.map((activity, key) => (
                      <div key={key}>
                        <Text element="p">
                          {intl.formatMessage(
                            findAvailableActivities(activity),
                          )}
                        </Text>
                        {findAllowedActivities(activity) && (
                          <Description
                            description={findAvailableActivityDescriptions(
                              activity,
                            )}
                            textSmall
                          />
                        )}
                      </div>
                    ))}
                    {hasOtherActivities && (
                      <div>
                        <Text element="p">
                          {intl.formatMessage(otherActivities())}
                        </Text>
                        {faqData["misc_activities"]?.description && (
                          <Description
                            description={
                              faqData["misc_activities"]?.description
                            }
                            textSmall
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {hasSpecifiedProhibitedActivities && (
                <>
                  <hr className="tw-border-gray-200 tw-my-16" />
                  <div className="tw-space-y-8">
                    <Text typeface="body-2-m" element="p">
                      {intl.formatMessage(
                        miscEventFaqDefinitions.activities_not_permitted_header,
                      )}
                    </Text>
                    {prohibitedActivities.map((activity, key) => (
                      <Text element="p" key={key}>
                        {intl.formatMessage(findProhibitedActivities(activity))}
                      </Text>
                    ))}
                    {hasOtherProhibitedActivity && (
                      <Text element="p">
                        {faqData["other_prohibited_activity"]}
                      </Text>
                    )}

                    <div className="tw-flex tw-p-8 tw-text-yellow-900 tw-bg-yellow-200">
                      <InfoFilled />
                      <Text
                        typeface="body-1"
                        className="tw-ml-8 tw-flex tw-items-center"
                      >
                        {intl.formatMessage(
                          miscEventFaqDefinitions.other_prohibited_activities_tips_guest,
                        )}
                      </Text>
                    </div>
                  </div>
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default injectIntl(EventActivities);
